import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { key: "proposal", title: "Client Proposal", hint: "Describe the client + project context. Get a structured proposal with 50/25/25 milestones." },
  { key: "brief", title: "Creative Brief", hint: "Describe the deliverable. Get a tight creative brief." },
  { key: "idea", title: "Content Ideas", hint: "Describe a niche or topic. Get 5 distinctive content ideas." },
];

export default function AIAssistant() {
  const [kind, setKind] = useState("proposal");
  const [context, setContext] = useState("");
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!context.trim()) return toast.error("Add some context");
    setLoading(true);
    setOut("");
    try {
      const { output } = await api.ai(kind, context);
      setOut(output);
    } catch (e) {
      toast.error("AI error");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(out);
    toast.success("Copied");
  };

  return (
    <div className="space-y-6" data-testid="ai-studio">
      <div>
        <div className="mono text-white/40">STUDIO · AI</div>
        <h1 className="text-4xl font-extrabold mt-2 flex items-center gap-3">AI Studio <Sparkles className="w-7 h-7 text-kinetic animate-pulse" /></h1>
        <p className="text-white/60 mt-2">Claude Sonnet 4.5 · purpose-built prompts for proposals, briefs, and content ideas.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-white/10 bg-[#0C0C0C] p-5 space-y-4">
          <div className="flex gap-1">
            {KINDS.map(k => (
              <button
                key={k.key}
                data-testid={`kind-${k.key}`}
                onClick={() => setKind(k.key)}
                className={`mono px-3 h-9 rounded-sm border ${kind === k.key ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/60 hover:text-white"}`}
              >
                {k.title.split(" ")[0].toUpperCase()}
              </button>
            ))}
          </div>
          <div className="mono text-white/50">{KINDS.find(k => k.key === kind).hint}</div>
          <Textarea
            data-testid="ai-context"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g. Client is an indie skincare brand looking for a 60s hero film + 6 reels. Budget $40k, 6 weeks. Target Gen Z."
            className="bg-black border-white/15 rounded-sm min-h-40"
          />
          <Button data-testid="ai-run" onClick={run} disabled={loading} className="kinetic-btn rounded-sm w-full">
            {loading ? "GENERATING…" : `Generate ${KINDS.find(k => k.key === kind).title}`}
          </Button>
        </div>

        <div className="border border-white/10 bg-[#0C0C0C] ai-glow">
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
            <span className="mono text-kinetic">OUTPUT · CLAUDE 4.5</span>
            {out && <button onClick={copy} className="mono text-white/60 hover:text-kinetic inline-flex items-center gap-1" data-testid="ai-copy"><Copy className="w-3 h-3" /> COPY</button>}
          </div>
          <div className="p-5 max-h-[60vh] overflow-y-auto whitespace-pre-wrap leading-relaxed text-white/85" data-testid="ai-output">
            {loading && <div className="mono text-kinetic animate-pulse">THINKING…</div>}
            {!loading && !out && <div className="mono text-white/30">OUTPUT WILL APPEAR HERE</div>}
            {!loading && out}
          </div>
        </div>
      </div>
    </div>
  );
}
